import React, { useEffect, useState } from 'react';
import Navbar from '../../Components/Common/Navbar';
import Footer from '../../Components/Common/Footer';
import { Container } from '../../Components/ui/Container';
import { Button } from '../../Components/ui/Button';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { COUPONS } from '../../Constants/Packages';
import { CustomSelect, CustomSelectItem } from '../../Components/Common/CustomSelect';
import { Label } from '../../Components/ui/Label';
import { DatePicker } from '../../Components/Common/DatePicker';
import { AlertBox } from '../../Components/Common/AlertBox';
import { cn } from '../../lib/cn';
import { format } from 'date-fns';
import { getImagePath, checkLPData } from '../../Utils/Utilities';
import { DemoBookingFlow } from '../Demo/BookDemo';
import apiService from '../../Utils/APIService';
import { useSearchParams } from 'react-router-dom';

const FeedBack = ({ brand }) => {
    const isMobile = useIsMobile();

    // Get query parameters
    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    };

    const [searchParams] = useSearchParams();
    useEffect(() => {
        checkLPData('feedback', searchParams);
    }, []);

    const isInteractionMode = getQueryParam('Ixn') === '1';
    const agentName = getQueryParam('agent') || '';

    const [selectedOption, setSelectedOption] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [interactionFeedback, setInteractionFeedback] = useState({
        helpfulness: null,
        enjoyment: null,
        clarity: null,
        wouldRecommend: null,
        overallRating: null
    });
    const [showThankYou, setShowThankYou] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showBusinessQuestions, setShowBusinessQuestions] = useState(false);

    const handleUseCoupon = (couponCode, editionId) => {
        const allDataRaw = localStorage.getItem('allData');
        if (!allDataRaw) return;

        const allData = JSON.parse(allDataRaw);
        const leadInfo = allData.info ? JSON.parse(allData.info) : [];
        const leadO = leadInfo[0]?.LeadO || '';
        const mobile = allData.mobile || '';

        const couponDetails = Object.values(COUPONS).find(
            (c) => c.code === couponCode && c.editionID === editionId
        );

        let selectedPeriod = "YY";
        if (couponDetails) {
            selectedPeriod = couponDetails.planYear === 1 ? "YY" : `YY${couponDetails.planYear}`;
        }

        localStorage.setItem("selEdn", editionId);
        localStorage.setItem("selPrd", selectedPeriod);
        localStorage.setItem(couponDetails?.planYear === 2 ? "cpnYY2" : "cpn", couponCode);
        localStorage.setItem("guidePG", window.location.pathname.substring(1));

        const cioData = [{ 'i': mobile, 'o': leadO }];
        localStorage.setItem('cio', JSON.stringify(cioData));

        window.location.href = "/Html/External/BZPay.html";
    };

    const handleBackNavigation = () => {
        if (showThankYou) {
            setShowThankYou(false);
            return;
        }

        if (showBusinessQuestions) {
            setShowBusinessQuestions(false);
            return;
        }

        if (selectedOption) {
            setSelectedOption(null);
            setFeedbackText('');
            setShowBusinessQuestions(false);
            return;
        }

        window.history.back();
    };

    // Handle browser back button
    React.useEffect(() => {
        const handlePopState = (event) => {
            event.preventDefault();
            handleBackNavigation();
        };

        // Push initial state
        window.history.pushState({ page: 'initial' }, '', window.location.href);

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [selectedOption, showBusinessQuestions, showThankYou]);

    React.useEffect(() => {
        if (selectedOption) {
            window.history.pushState({ page: 'step2', option: selectedOption }, '', window.location.href);
        }
    }, [selectedOption]);

    React.useEffect(() => {
        if (showBusinessQuestions) {
            window.history.pushState({ page: 'business' }, '', window.location.href);
        }
    }, [showBusinessQuestions]);

    // Get active 1-year coupons for QikERP Silver and Gold only
    const getYearlyCoupons = () => {
        return Object.values(COUPONS).filter(coupon =>
            coupon.isActive &&
            coupon.planYear === 1 &&
            (coupon.editionID === 34 || coupon.editionID === 35) // QikERP Silver (34) and Gold (35)
        );
    };
    const handleSubmitFeedback = async () => {
        const i = getQueryParam('i') || '';
        const o = getQueryParam('o') || '';

        setIsSubmitting(true);
        try {
            if (!isInteractionMode && selectedOption === 4 && feedbackText.trim()) {
                await apiService.setLeadFdbkWsp(i, o, feedbackText);
            }
            if (isInteractionMode && feedbackText.trim()) {
                await apiService.setLeadFdbkWsp(i, o, feedbackText);
            }
            setShowThankYou(true);
        } catch (error) {
            console.error("Failed to send feedback alert");
            setShowThankYou(true); // Still show thank you to user even if alert fails
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBookDemo = () => {
        // Navigate to demo booking or trigger modal
        window.location.href = '/book-demo';
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return "--";
        const date = new Date(dateString);
        const currentYear = new Date().getFullYear();
        const targetYear = date.getFullYear();
        const day = date.getDate();
        const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const month = monthNames[date.getMonth()];

        if (targetYear !== currentYear) {
            const yearShort = targetYear.toString().slice(-2);
            return `${day}-${month}-${yearShort}`;
        }

        return `${day}-${month}`;
    };

    const renderStarRating = (value, onChange) => {
        return (
            <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        onClick={() => onChange(star)}
                        className={`text-3xl transition-colors ${star <= value ? 'text-primary' : 'text-gray-300'
                            } hover:text-primary`}
                    >
                        ★
                    </button>
                ))}
            </div>
        );
    };

    const renderYesNoButtons = (value, onChange) => {
        return (
            <div className="flex gap-4 justify-center">
                <button
                    onClick={() => onChange('yes')}
                    className={`px-6 py-2 rounded font-semibold transition-all ${value === 'yes'
                        ? 'bg-primary text-white'
                        : 'bg-white border-2 border-border text-text-primary hover:border-primary'
                        }`}
                >
                    Yes
                </button>
                <button
                    onClick={() => onChange('no')}
                    className={`px-6 py-2 rounded font-semibold transition-all ${value === 'no'
                        ? 'bg-red-500 text-white'
                        : 'bg-white border-2 border-border text-text-primary hover:border-red-500'
                        }`}
                >
                    No
                </button>
            </div>
        );
    };

    // Non-Interaction Mode
    const renderPurchaseFeedback = () => {
        if (showThankYou) {
            return (
                <div className="text-center py-12">
                    <div className="mb-6">
                        <i className="fas fa-check-circle text-6xl text-primary"></i>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                        Thank You!
                    </h2>
                    <p className="text-lg text-text-primary">
                        We appreciate your valuable feedback.
                    </p>
                </div>
            );
        }

        return (
            <>
                {!selectedOption && (
                    <div className="mb-6 max-w-3xl mx-auto">
                        <div className="items-center justify-center mb-4">
                            <h3 className="text-[18px] md:text-[22px] font-semibold text-text-primary flex-1 md:flex-none text-center">
                                Are you planning to Purchase?
                            </h3>
                        </div>
                        {/* Options */}
                        <div className="space-y-3 md:space-y-4">
                            {[
                                { id: 1, text: 'Yes, Give me Discount!', icon: 'fa-tag' },
                                { id: 2, text: 'Yes, but not in the next 2 Months', icon: 'fa-calendar' },
                                { id: 3, text: 'Maybe later', icon: 'fa-clock' },
                                { id: 4, text: 'No', icon: 'fa-times-circle' }
                            ].map((option) => (
                                <button
                                    key={option.id}
                                    onClick={() => setSelectedOption(option.id)}
                                    className={`w-full p-3 md:p-4 rounded-[5px] border-2 text-left transition-all ${selectedOption === option.id
                                        ? 'bg-primary text-white border-primary shadow-medium'
                                        : 'bg-background-light border-border text-text-primary hover:border-primary hover:shadow-light'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <i className={`fas ${option.icon} text-sm`}></i>
                                        <span className="text-[14px] md:text-base font-semibold text-text-primary">{option.text}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {selectedOption === 1 && (
                    <div className="mb-12 max-w-5xl mx-auto">
                        {/* <button
                            onClick={handleBackNavigation}
                            className="flex items-center gap-2 text-primary hover:text-secondary transition-colors mb-6"
                        >
                            <i className="fas fa-arrow-left"></i>
                            <span className="font-semibold">Back</span>
                        </button> */}
                        <h3 className="text-[18px] md:text-[22px] font-semibold text-text-primary mb-4 text-center">
                            Exclusive Discount Coupons for You!
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            {getYearlyCoupons().map((coupon) => (
                                <div key={coupon.id} className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                                    <h4 className="text-base md:text-lg font-bold text-primary mb-2">
                                        {coupon.title}
                                    </h4>
                                    <p className="text-sm md:text-base text-text-primary mb-4">
                                        {coupon.description}
                                    </p>
                                    <div className="bg-white border border-border rounded-[5px] p-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex-1">
                                                <p className="text-xs text-text-secondary mb-1">Coupon Code:</p>
                                                <code className="font-mono font-semibold text-sm md:text-base text-primary">
                                                    {coupon.code}
                                                </code>
                                            </div>
                                            <Button
                                                onClick={() => handleUseCoupon(coupon.code, coupon.editionID)}
                                                size="sm"
                                            >
                                                <i className="fa fa-tag mr-1"></i> Get Offer
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-center gap-4 mt-8">
                            <Button
                                onClick={() => {
                                    const i = getQueryParam('i') || '';
                                    const o = getQueryParam('o') || '';
                                    window.location.href = `/QikERP/Guide?i=${i}&o=${o}&z=offer`;
                                }}
                                size={!isMobile ? "lg" : "default"}

                                variant="outline"
                            >
                                View Packages
                            </Button>
                            <Button
                                onClick={handleBackNavigation}
                                variant="outline"
                                size={!isMobile ? "lg" : "default"}

                            >
                                <i className="fas fa-arrow-left mr-2"></i>Back
                            </Button>
                        </div>
                    </div>
                )}

                {(selectedOption === 2 || selectedOption === 3) && (
                    <div className="transition-all duration-300 ease-in-out">
                        <DemoBookingFlow
                            onComplete={() => setShowThankYou(true)}
                            onBack={handleBackNavigation}
                            hideInitialBack={false}
                        />
                    </div>
                )}
                {selectedOption === 4 && (
                    <div className="mb-12 max-w-3xl mx-auto">
                        {/* <button
                            onClick={handleBackNavigation}
                            className="flex items-center gap-2 text-primary hover:text-secondary transition-colors mb-6"
                        >
                            <i className="fas fa-arrow-left"></i>
                            <span className="font-semibold">Back</span>
                        </button> */}
                        <div className="items-center justify-center mb-4">
                            <h3 className="text-[18px] md:text-[22px] font-semibold text-text-primary flex-1 md:flex-none text-center">
                                We'd love to hear your feedback
                            </h3>
                        </div>
                        <div className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                            <p className="text-[14px] text-text-secondary mb-3 text-center">
                                Please share why you're not interested. Your feedback helps us improve.
                            </p>
                            <textarea
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                placeholder="Share your thoughts here."
                                rows={5}
                                className="w-full p-3 border-2 border-border rounded-[5px] text-sm md:text-base bg-white text-text-primary focus:outline-none focus:border-primary hover:border-primary transition-all placeholder:text-[14px] text-[14px] placeholder:text-gray-400 resize-none"
                            />
                        </div>
                        <div className="mt-8 flex items-center justify-center gap-4">
                            <Button
                                onClick={handleSubmitFeedback}
                                size={!isMobile ? "lg" : "default"}
                                disabled={!feedbackText.trim() || isSubmitting}
                            >
                                Submit Feedback
                            </Button>
                            <Button onClick={handleBackNavigation} variant="outline"
                                size={!isMobile ? "lg" : "default"}
                            >
                                <i className="fas fa-arrow-left mr-2"></i>Back
                            </Button>
                        </div>
                    </div>
                )}
            </>
        );
    };

    // Interaction Mode
    const renderInteractionFeedback = () => {
        if (showThankYou) {
            return (
                <div className="text-center py-12">
                    <div className="mb-6">
                        <i className="fas fa-check-circle text-6xl text-primary"></i>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                        Thank You for Your Feedback!
                    </h2>
                    <p className="text-lg text-text-primary">
                        We truly appreciate you taking the time to share your experience with us.
                    </p>
                </div>
            );
        }

        return (
            <div className="mb-12 ">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h2 className="text-[30px] md:text-[40px] font-bold text-primary mb-4">
                        You interacted with {agentName || 'our team member'}
                    </h2>
                    <p className="text-lg md:text-xl text-text-primary">
                        We'd love to hear about your experience
                    </p>
                </div>

                {/* Feedback Questions */}
                <div className="max-w-3xl mx-auto space-y-10">
                    {/* Question 1: Overall Rating */}
                    {/* Question 1: Overall Rating */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-5 md:p-6">
                        <h3 className="text-base md:text-lg font-bold text-text-primary mb-5 text-center">
                            How would you rate your overall interaction?
                        </h3>
                        {renderStarRating(
                            interactionFeedback.overallRating,
                            (value) => setInteractionFeedback({ ...interactionFeedback, overallRating: value })
                        )}
                    </div>

                    {/* Question 2: Helpfulness */}
                    {/* Question 1: Overall Rating */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-5 md:p-6">
                        <h3 className="text-base md:text-lg font-bold text-text-primary mb-5 text-center">
                            Were they able to help you with your queries?
                        </h3>
                        {renderYesNoButtons(
                            interactionFeedback.helpfulness,
                            (value) => setInteractionFeedback({ ...interactionFeedback, helpfulness: value })
                        )}
                    </div>

                    {/* Question 3: Enjoyment */}
                    {/* Question 1: Overall Rating */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-5 md:p-6">
                        <h3 className="text-base md:text-lg font-bold text-text-primary mb-5 text-center">
                            Did you enjoy talking with them?
                        </h3>
                        {renderYesNoButtons(
                            interactionFeedback.enjoyment,
                            (value) => setInteractionFeedback({ ...interactionFeedback, enjoyment: value })
                        )}
                    </div>

                    {/* Question 4: Clarity */}
                    {/* Question 1: Overall Rating */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-5 md:p-6">
                        <h3 className="text-base md:text-lg font-bold text-text-primary mb-5 text-center">
                            Were the explanations clear and easy to understand?
                        </h3>
                        {renderYesNoButtons(
                            interactionFeedback.clarity,
                            (value) => setInteractionFeedback({ ...interactionFeedback, clarity: value })
                        )}
                    </div>

                    {/* Question 5: Recommendation */}
                    {/* Question 1: Overall Rating */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-5 md:p-6">
                        <h3 className="text-base md:text-lg font-bold text-text-primary mb-5 text-center">
                            Would you recommend our service to others?
                        </h3>
                        {renderYesNoButtons(
                            interactionFeedback.wouldRecommend,
                            (value) => setInteractionFeedback({ ...interactionFeedback, wouldRecommend: value })
                        )}
                    </div>

                    {/* Additional Comments */}
                    {/* Question 1: Overall Rating */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-5 md:p-6">
                        <h3 className="text-base md:text-lg font-bold text-text-primary mb-5 text-center">
                            Any additional comments or suggestions?
                        </h3>
                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Share your thoughts (optional)..."
                            rows={5}
                            className="w-full p-3 border-2 border-primary rounded-[5px] text-sm md:text-base text-text-primary bg-background-light focus:outline-none resize-none cursor-text"
                            autoFocus />
                    </div>

                    {/* Submit Button */}
                    <div className="text-center pt-4">
                        <Button
                            onClick={handleSubmitFeedback}
                            size={!isMobile ? "lg" : "default"}
                            disabled={!interactionFeedback.overallRating}
                        >
                            Submit Feedback
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    const featuresWithIcons = [
        { name: 'CRM', icon: 'fa fa-users' },
        { name: 'Quotes', icon: 'fas fa-file-alt' },
        { name: 'Orders', icon: 'fas fa-box' },
        { name: 'Billing', icon: 'fas fa-money-bill-wave' },
        { name: 'E-Way Bill', icon: 'fas fa-truck' },
        { name: 'E-Invoice', icon: 'fas fa-file-invoice' },
        { name: 'Accounts', icon: 'fas fa-inr' },
        // {name: 'GSTR1 & 3B', icon: 'fas fa-landmark' },
        { name: 'Recovery', icon: 'fas fa-hand-holding-usd' },
        { name: 'Inventory', icon: 'fas fa-warehouse' },
        // {name: 'Batches', icon: 'fas fa-layer-group' },
        // {name: 'BOM', icon: 'fas fa-clipboard-list' },
        // {name: 'Purchase Orders', icon: 'fa fa-shopping-cart' },
        // {name: 'Suppliers Invoices', icon: 'fas fa-user-tie' },
        // {name: 'MRP', icon: 'fas fa-calculator' },
        { name: 'Projects', icon: 'fas fa-project-diagram' },
        { name: 'Manufacturing', icon: 'fas fa-industry' },
        // {name: 'Tasks', icon: 'fas fa-tasks' },
        // {name: 'Support Ticketing', icon: 'fas fa-headset' },
        // {name: 'Contracts', icon: 'fas fa-file-contract' },
    ];

    const handleBuyNow = () => {
        const i = getQueryParam('i') || '';
        const o = getQueryParam('o') || '';
        window.location.href = `/QikERP/Guide?i=${i}&o=${o}&z=offer`;
    };

    return (
        <div className="min-h-screen bg-white md:pt-16 flex flex-col">
            {!isMobile && <Navbar logo={brand === 'bz' ? getImagePath("/Images/Biziverse.png") : getImagePath("/Images/QikERP.png")} onBuyNowClick={handleBuyNow} showBuyNow={true} />}

            <main className="flex-grow flex" id="feedback-main">
                <section className="bg-white pt-8 md:pt-16 lg:pt-20 w-full">
                    {!isMobile ? (
                        <div className="w-full max-w-[1600px] mx-auto px-8">
                            <div className="grid grid-cols-2 gap-16 items-start">
                                {/* Left Column - Brand Panel */}
                                <div className="flex flex-col justify-start sticky top-20 pt-0">
                                    {/* Logo */}
                                    <div className="flex flex-col items-start mb-8">
                                        {brand === 'bz' ? (
                                            <img
                                                src={getImagePath("/Images/Biziverse.png")}
                                                alt="Biziverse"
                                                className="h-[95px] w-auto object-contain"
                                            />
                                        ) : (
                                            <div className="relative inline-flex flex-col items-start">
                                                <img
                                                    src={getImagePath("/Images/QikERP.png")}
                                                    alt="QikERP"
                                                    className="h-[95px] w-auto object-contain"
                                                />
                                                <div className="flex flex-col items-center absolute" style={{ bottom: '-38px', right: '8px' }}>
                                                    <span className="text text-primary font-bold leading-none mb-0.5">by</span>
                                                    <img
                                                        src={getImagePath("/Images/Biziverse.png")}
                                                        alt="Biziverse"
                                                        className="h-[32px] w-auto object-contain"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="h-[38px]" />
                                    </div>

                                    {/* Tagline */}
                                    <h1 className="text-[40px] font-bold text-left !leading-[1.25] mb-8">
                                        <span className="text-primary">Easy</span> & <span className="text-primary">Powerful</span> Tools to
                                        <br />
                                        <span className="text-primary">Accelerate Business</span>
                                    </h1>


                                    {/* Feature Tags */}
                                    <div className="flex flex-wrap gap-3 mb-10">
                                        {featuresWithIcons.map((feature, idx) => (
                                            <div key={idx} className="flex items-center gap-2 border border-border rounded px-3 py-2 bg-white">
                                                <i className={`${feature.icon} flex-shrink-0 text-primary`} style={{ fontSize: '16px' }}></i>
                                                <span className="text-sm text-text-primary font-bold">{feature.name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Buy Now & Compare Plans Buttons */}
                                    <div className="flex gap-3">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            onClick={() => {
                                                const pricingSection = document.getElementById('simplePricing');
                                                if (pricingSection) {
                                                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                                                } else {
                                                    window.location.href = '/QikERP/#simplePricing';
                                                }
                                            }}
                                        >
                                            Buy Now
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => {
                                                const pricingSection = document.getElementById('simplePricing');
                                                if (pricingSection) {
                                                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                                                } else {
                                                    window.location.href = '/QikERP/#simplePricing';
                                                }
                                            }}
                                        >
                                            Compare Plans
                                        </Button>
                                    </div>
                                </div>

                                {/* Right Column - Feedback Content */}
                                <div className="py-4 pt-0">
                                    <h1 className="text-[40px] font-bold text-primary mb-4 text-center">
                                        {(selectedOption === 2 || selectedOption === 3) ? 'Book a Free Demo' : 'Request for Feedback'}
                                    </h1>
                                    {isInteractionMode ? renderInteractionFeedback() : renderPurchaseFeedback()}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Container>
                            {/* Mobile: Logo + by Biziverse stacked */}
                            <div className="flex flex-col items-center mb-6 pt-8">
                                {brand === 'bz' ? (
                                    <img
                                        src={getImagePath("/Images/Biziverse.png")}
                                        alt="Biziverse"
                                        className="h-[70px] w-auto object-contain"
                                    />
                                ) : (
                                    <div className="relative inline-flex flex-col items-center">
                                        <img
                                            src={getImagePath("/Images/QikERP.png")}
                                            alt="QikERP"
                                            className="h-[85px] w-auto object-contain"
                                        />
                                        <div className="flex flex-col items-center absolute" style={{ bottom: '-25px', right: '8px' }}>
                                            <span className="text-sm text-primary font-bold leading-none mb-0.5">by</span>
                                            <img
                                                src={getImagePath("/Images/Biziverse.png")}
                                                alt="Biziverse"
                                                className="h-[26px] w-auto object-contain"
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="h-[38px]" />
                            </div>
                            {/* Mobile: Main Heading */}
                            <h1 className="text-[28px] font-bold text-primary text-center mb-8">
                                {(selectedOption === 2 || selectedOption === 3) ? 'Book a Free Demo' : 'Request for Feedback'}
                            </h1>

                            {isInteractionMode ? renderInteractionFeedback() : renderPurchaseFeedback()}
                        </Container>
                    )}
                </section>
            </main>
            <Footer className="!bg-background-light" />
        </div>
    );
};

export default FeedBack;